import type { AxiosResponse } from "axios";
import { BASE_URL, httpClient } from "../config/develop-config";

const baseUrl = `${BASE_URL}/crm/tier-program`;

const getTierProgramSettingList = async (params: { company: string }) => {
  return await httpClient
    .get(`${baseUrl}/get-tier-program-setting-list`, { params })
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const getTierAvailableYear = async (params: { company: string, isHistory?: boolean }) => {
  return await httpClient
    .get(`${baseUrl}/available-years`, { params })
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const getTierProgramTierLevel = async (programId: string) => {
  return await httpClient
    .get(`${baseUrl}/get-tier-level-list/${programId}`)
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const getTierProgramDetail = async (programId: string) => {
  return await httpClient
    .get(`${baseUrl}/get-tier-program-detail/${programId}`)
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const createTierProgram = async (
  company : string,
  year : number,
  faqs : string,
  termsAndConditions : string,
  valueAfter : any,
  updatedBy : string,
  programImageUrl? : File,
  programImageUrlPath? : string,
) => {
  const formData = new FormData();
  formData.append("company", company);
  formData.append("year", String(year));
  formData.append("faqs", faqs);
  formData.append("termsAndConditions", termsAndConditions);
  if (programImageUrl) {
    formData.append("programImageUrl", programImageUrl);
  }
  if (programImageUrlPath) {
    formData.append("programImageUrlPath", programImageUrlPath);
  }
  formData.append("valueAfter", valueAfter);
  formData.append("updatedBy", updatedBy);

  return await httpClient
    .post(`${baseUrl}/create-tier-program/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const updateTierProgram = async (
  tierProgramId : string,
  faqs : string,
  termsAndConditions : string,
  valueBefore : string,
  valueAfter : string,
  remark : string,
  updatedBy : string,
  programImageUrl? : File,
) => {
    const formData = new FormData();
    formData.append("tierProgramId", tierProgramId);
    formData.append("remark", remark);
    formData.append("valueBefore", valueBefore);
    formData.append("valueAfter", valueAfter);
    formData.append("faqs", faqs);
    formData.append("termsAndConditions", termsAndConditions);
    formData.append("updatedBy", updatedBy);
    if (programImageUrl) {
      formData.append("programImageUrl", programImageUrl);
    }
    return await httpClient
      .post(`${baseUrl}/update-tier-program/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res: AxiosResponse) => res.data)
      .catch((err) => console.log(err));
}

const toggleRebatePercent = async (tierLevelId: string, isShowRebatePercent : boolean, updatedBy: string) => {
  return await httpClient
    .post(`${baseUrl}/toggle-rebate-percent`, { tierLevelId, isShowRebatePercent, updatedBy })
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const upsertHowItWorks = async (tierProgramId: string, howItWorks: string, imageUrl : File[], updatedBy: string) => {
  const formData = new FormData();
  formData.append("tierProgramId", tierProgramId);
  formData.append("howItWorks", howItWorks);
  imageUrl.forEach((file) => formData.append("imageUrl", file));
  formData.append("updatedBy", updatedBy);

  return await httpClient
    .post(`${baseUrl}/upsert-how-it-works`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

const updateTierProgramFiles = async (
  tierProgramId : string,
  programImage : File, 
  programImageAction : string,
  updatedBy : string
) => {
  const formData = new FormData();
  formData.append("tierProgramId", tierProgramId);
  formData.append("programImage", programImage);
  formData.append("programImageAction", programImageAction);
  formData.append("updatedBy", updatedBy);

  return await httpClient
    .post(`${baseUrl}/update-tier-program-files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res: AxiosResponse) => {
      return res.data;
    })
    .catch((err) => console.log(err));
}

const upsertTierLevel = async (
  tierProgramId : string,
  tierRank : number,
  tierName : string,
  tierLevelBenefits : string,
  updatedBy : string,
  rebateTitle : string,
  rebateDetail : string,
  rebatePercent : number,
  color : string,
  isShowRebatePercent: boolean,
  isNotAutoDemotable: boolean,
  reviewTriggerValue?: number,
  benefitImageUrl? : File[],
  tierImageUrl? : File, 
  tierImageUrlPath? : string,
  tierBgImageUrl? : File, 
  tierBgImageUrlPath? : string,
) => {
  const formData = new FormData();
  formData.append("tierProgramId", tierProgramId);
  formData.append("tierRank", String(tierRank));
  formData.append("tierName", tierName);
  formData.append("tierLevelBenefits", tierLevelBenefits);
  formData.append("rebateTitle", rebateTitle);
  formData.append("rebateDetail", rebateDetail);
  formData.append("rebatePercent", String(rebatePercent));
  formData.append("color", color);
  formData.append("isShowRebatePercent", String(isShowRebatePercent));
  formData.append("isNotAutoDemotable", String(isNotAutoDemotable));
  formData.append("updatedBy", updatedBy);
  if ((reviewTriggerValue !== undefined && reviewTriggerValue !== null) && tierRank > 1) {
    formData.append("reviewTriggerValue", String(reviewTriggerValue));
  }

  if (benefitImageUrl) {
    benefitImageUrl.forEach((file) => formData.append("benefitImageUrl", file));
  }

  if (tierImageUrl) {
    formData.append("tierImageUrl", tierImageUrl);
  }
  if (tierImageUrlPath) {
    formData.append("tierImageUrlPath", tierImageUrlPath);
  }
  if (tierBgImageUrl) {
    formData.append("tierBgImageUrl", tierBgImageUrl);
  }
  if (tierBgImageUrlPath) {
    formData.append("tierBgImageUrlPath", tierBgImageUrlPath);
  }

  return await httpClient
    .post(`${baseUrl}/upsert-tier-level`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res: AxiosResponse) => {
      return res.data;
    })
    .catch((err) => console.log(err));
}

const deleteTierProgram = async(tierProgramId : string) => {
  return await httpClient
    .delete(`${baseUrl}/delete-tier-program/${tierProgramId}`)
    .then((res: AxiosResponse) => res.data)
    .catch((err) => console.log(err));
};

export { getTierProgramSettingList, getTierProgramDetail, getTierProgramTierLevel, toggleRebatePercent, getTierAvailableYear, upsertHowItWorks, createTierProgram, updateTierProgramFiles, upsertTierLevel, deleteTierProgram, updateTierProgram };
